import {ClockHoursTranscriptSection, ClockHoursTranscriptSectionCourse} from "types/clock-hours-transcript"
import styles from "./ProgressReportPdf.module.css"
import {chunk, cloneDeep, keyBy} from "lodash"
import {formatDecimal, formatDecimalWithoutRound} from "helpers"
import {Course} from "types/courses"

const COLUMNS_COUNT = 3
const EXAM_SUBMISSIONS_TYPE = "Exam"

type PracticalCourseForTranscript = {
    courseId: number
    totalQuantity?: number | string
    credits?: number | string
    submodules?: Array<{
        quantity: number
        units?: Course.Unit | string
        submittedSyncedCount?: number
        submittedCompletedCount?: number
        submittedCompletedHours?: number
        submissionsType?: string
    }>
}

type CoursePracticalStats = {
    result?: number
    min?: number
    rem?: number | string | null
}

type ClockHoursTranscriptSectionsProps = {
    data: ClockHoursTranscriptSection[]
    practicalCourses?: PracticalCourseForTranscript[]
}

const toNumber = (value: number | string | null | undefined): number | undefined => {
    if (value === null || value === undefined || value === "") {
        return undefined
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

/** Same aggregation as Practical CourseSection completed total (Credit count or Hour hours). */
const getCompletedQuantityForSubmodule = (submodule: {
    quantity: number
    units?: Course.Unit | string
    submittedCompletedCount?: number
    submittedCompletedHours?: number
}): number => {
    const completedValue =
        submodule.units === Course.Unit.Hour
            ? submodule.submittedCompletedHours || 0
            : submodule.submittedCompletedCount || 0
    return Math.min(completedValue, submodule.quantity || 0)
}

const getCoursePracticalStats = (
    course: ClockHoursTranscriptSectionCourse,
    practicalCoursesById: Record<number, PracticalCourseForTranscript>
): CoursePracticalStats => {
    if (!course?.courseId) {
        return {}
    }

    const practicalCourse = practicalCoursesById[course.courseId]
    const submodules = practicalCourse?.submodules ?? []
    const minQuantity = toNumber(practicalCourse?.totalQuantity ?? practicalCourse?.credits ?? course.credits)
    const isExam =
        Boolean(Number(course.isExam)) ||
        submodules.some((submodule) => submodule.submissionsType === EXAM_SUBMISSIONS_TYPE)
    const minimumPassingGrade = toNumber(course.minimumPassingGrade)

    let result: number | undefined
    if (isExam) {
        result = toNumber(course.examGrade)
    } else if (practicalCourse) {
        // Non-exam Result = completed credit/hours (matches Practical "Credit (Completed) X/Y")
        const completedTotal = submodules.reduce(
            (total, submodule) => total + getCompletedQuantityForSubmodule(submodule),
            0
        )
        if (completedTotal > 0) {
            result = completedTotal
        }
    }

    let rem: number | string | null = null
    if (isExam) {
        if (typeof result === "number" && typeof minimumPassingGrade === "number") {
            rem = result >= minimumPassingGrade ? "PASS" : "FAIL"
        }
    } else if (typeof minQuantity === "number") {
        rem = Math.max(minQuantity - (result ?? 0), 0)
    }

    return {
        result,
        min: minQuantity,
        rem
    }
}

export const ClockHoursTranscriptSections = ({data, practicalCourses = []}: ClockHoursTranscriptSectionsProps) => {
    if (!data?.length) {
        return null
    }

    const practicalCoursesById = keyBy(practicalCourses, "courseId")

    return (
        <div>
            {data.map((section) => {
                let courses = cloneDeep(section.courses)
                const coursesPerColumn = Math.max(1, Math.ceil(courses.length / COLUMNS_COUNT))
                const additionalCoursesCount = coursesPerColumn * COLUMNS_COUNT - courses.length
                if (additionalCoursesCount > 0) {
                    courses = courses.concat(
                        Array(additionalCoursesCount).fill({courseName: "", courseCode: "", credits: undefined})
                    )
                }
                return (
                    <div className={styles.tableContainer} key={section.sectionId}>
                        <div className={styles.subjectBlock}>
                            <div style={{width: "50%"}}>
                                <span className={styles.subjectTitle}>Subject:</span>
                                <span className={styles.subjectValue}>{section.sectionTitle}</span>
                            </div>
                            <div>
                                <span className={styles.subjectTitle}>Grade:</span>
                                <span className={styles.subjectValue}>
                                    {typeof section.avgGpa === "number" ? formatDecimal(section.avgGpa) : "NA"}
                                </span>
                            </div>
                            <div>
                                <span className={styles.subjectTitle}>Completed:</span>
                                <span className={styles.subjectValue}>No</span>
                            </div>
                        </div>
                        <div className={styles.tableBlockClockHoursTranscript}>
                            {chunk(courses, coursesPerColumn).map((columnCourses, columnIndex) => {
                                return (
                                    <table
                                        key={`${section.sectionId}-${columnIndex}`}
                                        className={styles.tableClockHoursTranscript}>
                                        <thead>
                                            <tr>
                                                <th />
                                                <th>Result</th>
                                                <th>Min</th>
                                                <th>Rem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {columnCourses.map((course, courseIndex) => {
                                                const {
                                                    result,
                                                    min: minQuantity,
                                                    rem
                                                } = getCoursePracticalStats(course, practicalCoursesById)
                                                return (
                                                    <tr key={course.courseId ?? `${columnIndex}-${courseIndex}`}>
                                                        <td>{course.courseName}</td>
                                                        <td>
                                                            {typeof result === "number"
                                                                ? formatDecimalWithoutRound(result)
                                                                : ""}
                                                        </td>
                                                        <td>{typeof minQuantity === "number" ? minQuantity : ""}</td>
                                                        <td>
                                                            {typeof rem === "number"
                                                                ? formatDecimalWithoutRound(rem)
                                                                : rem ?? ""}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )
                            })}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
